import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Plus, X, Upload, Globe, Twitter, Linkedin } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface SocialLink {
  platform: string;
  url: string;
}

const SponsorOnboarding = () => {
  const navigate = useNavigate();
  const { user, registerUser } = useAuth();
  const [profilePic, setProfilePic] = useState<File | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [description, setDescription] = useState('');
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [newSocialPlatform, setNewSocialPlatform] = useState('');
  const [newSocialUrl, setNewSocialUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setProfilePic(e.target.files[0]);
    }
  };

  const addSocialLink = () => {
    if (newSocialPlatform.trim() && newSocialUrl.trim()) {
      setSocialLinks([...socialLinks, { platform: newSocialPlatform.trim(), url: newSocialUrl.trim() }]);
      setNewSocialPlatform('');
      setNewSocialUrl('');
    }
  };

  const removeSocialLink = (index: number) => {
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      // Convert profile picture to base64 if exists
      const profilePicBase64 = profilePic ? await convertFileToBase64(profilePic) : null;

      // Register the user
      await registerUser({
        role: 'sponsor',
        companyName,
        companyUrl,
        companyDescription: description,
        socialLinks,
        profilePic: profilePicBase64
      });

      // Navigate to bounties page on success
      navigate('/bounties');
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to convert File to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  return (
    <div className="min-h-screen bg-[#FAF94F] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-black font-['OT_Bulb_Classic'] uppercase tracking-wide">Complete Your Sponsor Profile</h1>
          <p className="mt-2 text-gray-800 font-['Public_Sans']">Tell us about your company to get started</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 border-2 border-red-500 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8 bg-white p-8 rounded-lg border-2 border-black shadow-[4px_4px_0px_black]">
          {/* Profile Picture */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Company Logo</label>
            <div className="flex items-center space-x-4">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                {profilePic ? (
                  <img
                    src={URL.createObjectURL(profilePic)}
                    alt="Company Logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Upload className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePicChange}
                  className="hidden"
                  id="profile-pic"
                />
                <label
                  htmlFor="profile-pic"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Upload Logo
                </label>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Name</label>
              <Input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                className="mt-1"
                placeholder="Enter your company name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Website</label>
              <Input
                type="url"
                value={companyUrl}
                onChange={(e) => setCompanyUrl(e.target.value)}
                required
                className="mt-1"
                placeholder="https://your-company.com"
              />
            </div>
          </div>

          {/* Company Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700">About Your Company</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              className="mt-1"
              rows={4}
              placeholder="Tell us about your company, its mission, and what you're looking for in builders..."
            />
          </div>

          {/* Social Links */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Social Links</label>
            <div className="mt-2 space-y-2">
              {socialLinks.map((link, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={link.platform}
                    disabled
                    className="flex-1"
                  />
                  <Input
                    type="text"
                    value={link.url}
                    disabled
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={() => removeSocialLink(index)}
                    variant="destructive"
                    size="sm"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newSocialPlatform}
                  onChange={(e) => setNewSocialPlatform(e.target.value)}
                  placeholder="Platform (e.g., Twitter)"
                  className="flex-1"
                />
                <Input
                  type="text"
                  value={newSocialUrl}
                  onChange={(e) => setNewSocialUrl(e.target.value)}
                  placeholder="URL"
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={addSocialLink}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#FAF94F] text-black hover:bg-[#f7e359] border-2 border-black shadow-[2px_2px_0px_black] py-6 text-lg font-['Public_Sans'] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Complete Profile'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SponsorOnboarding; 